import DataType from '@opsbase/lib-datatype/DataType';
import i18n from '@opsbase/lib-i18n';
import Palette from '@opsbase/lib-palette';
import SPTType from '@opsbase/lib-spt/SPTType';
import useSelf from '@opsbase/react-ctrl-self/useSelf';
import useIssueFindById from '@opsbase/react-gql-hooks/issue/useIssueFindById';
import useIssueUpdate from '@opsbase/react-gql-hooks/issue/useIssueUpdate';
import DataPill from '@opsbase/web-chips/DataPill';
import ConversationContainer from '@opsbase/web-conversation-views/src/ConversationContainer';
import useNewDataActions from '@opsbase/web-ctrl-newdata/useNewDataActions';
import SPTChooser from '@opsbase/web-data-choosers/SPTChooser';
import Dialog from '@opsbase/web-dialogs/Dialog';
import DialogSize from '@opsbase/web-dialogs/Dialog/DialogSize';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Pane from '@opsbase/web-layout/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import useNav from '@opsbase/web-nav/useNav';
import useParams from '@opsbase/web-nav/useParams';
import Button from '@opsbase/web-ui/Button';
import Checkbox from '@opsbase/web-ui/Checkbox';
import Spinner from '@opsbase/web-ui/Spinner';
import Text from '@opsbase/web-ui/Text';
import React, { useCallback, useState } from 'react';
export function IssueReassignDialog() {
    var _a, _b, _c, _d, _e, _f;
    const { id } = useParams();
    const [busy, setBusy] = useState(false);
    const [assigneeType, setAssigneeType] = useState('other');
    const [otherSPTType, setOtherSPTType] = useState(null);
    const [otherAllocatee, setOtherAllocatee] = useState(null);
    const { loading, data: issue } = useIssueFindById({ variables: { id } });
    const self = useSelf();
    const { goBack } = useNav();
    const updateIssue = useIssueUpdate();
    const { updateItemsForType } = useNewDataActions();
    const creatorIsMe = ((_a = issue === null || issue === void 0 ? void 0 : issue.createdBy) === null || _a === void 0 ? void 0 : _a.id) === self.id;
    const iCanHaveIt = self.isStaff && ((_b = issue === null || issue === void 0 ? void 0 : issue.assignedToStaff) === null || _b === void 0 ? void 0 : _b.id) !== self.id;
    const creatorCanHaveIt = !!(issue === null || issue === void 0 ? void 0 : issue.createdBy) &&
        ((_c = issue === null || issue === void 0 ? void 0 : issue.assignedToStaff) === null || _c === void 0 ? void 0 : _c.id) !== ((_d = issue === null || issue === void 0 ? void 0 : issue.createdBy) === null || _d === void 0 ? void 0 : _d.id) &&
        !!((_e = issue === null || issue === void 0 ? void 0 : issue.createdBy) === null || _e === void 0 ? void 0 : _e.enabled);
    const handleConfirmClick = useCallback(async () => {
        let aType, aStaff, aPosition, aTeam;
        switch (assigneeType) {
            case 'me':
                aType = SPTType.STAFF;
                aStaff = { id: self.id };
                break;
            case 'reporter':
                aType = SPTType.STAFF;
                aStaff = issue === null || issue === void 0 ? void 0 : issue.createdBy;
                break;
            case 'other':
                aType = otherSPTType;
                aStaff = otherSPTType === SPTType.STAFF ? otherAllocatee : null;
                aPosition =
                    otherSPTType === SPTType.POSITION ? otherAllocatee : null;
                aTeam = otherSPTType === SPTType.TEAM ? otherAllocatee : null;
                break;
        }
        setBusy(true);
        setTimeout(async () => {
            let patch = {
                assignedToSPTType: aType,
                assignedToStaff: aStaff ? { id: aStaff.id } : null,
                assignedToPosition: aPosition ? { id: aPosition.id } : null,
                assignedToTeam: aTeam ? { id: aTeam.id } : null,
            };
            const i = await updateIssue({
                id: issue === null || issue === void 0 ? void 0 : issue.id,
                patch,
            });
            updateItemsForType(DataType.ISSUE, [i]);
            goBack();
        }, 0);
    }, [
        assigneeType,
        goBack,
        issue,
        otherAllocatee,
        otherSPTType,
        self,
        updateIssue,
        updateItemsForType,
    ]);
    const handleChangeAssignee = useCallback((newAssigneeType, allocatee) => {
        setAssigneeType(newAssigneeType);
        setOtherSPTType((allocatee === null || allocatee === void 0 ? void 0 : allocatee.length) ? allocatee[0].type : null);
        setOtherAllocatee((allocatee === null || allocatee === void 0 ? void 0 : allocatee.length) ? allocatee[0] : null);
    }, []);
    const handleClose = useCallback(() => {
        goBack();
    }, [goBack]);
    return (React.createElement(Dialog, { padded: true, loading: loading, title: i18n.t('reassign-issue'), size: DialogSize.X800_Y400, buttonsLeft: React.createElement(Button, { onClick: handleClose, color: Palette.Grey, disabled: busy, tabIndex: 1, label: i18n.t('cancel') }), buttonsRight: React.createElement(Button, { onClick: handleConfirmClick, color: Palette.positive, disabled: busy, tabIndex: 0, label: i18n.t('reassign-issue') }) },
        React.createElement(Spinner, { whiteout: true, busy: busy, startDelay: 0 },
            React.createElement(Horizontal, { stretch: true, gutter: true, style: { minHeight: 350 } },
                React.createElement(Vertical, { gutter: true },
                    React.createElement(Vertical, { padding: true, shrink: true },
                        React.createElement(Text, { variant: "h3" }, i18n.t('reassign-step2-help')),
                        !!iCanHaveIt && (React.createElement(Horizontal, { justify: 'flex-start', centered: true },
                            React.createElement(Checkbox, { color: Palette.GoodOldBlue, checked: assigneeType === 'me', onChange: () => handleChangeAssignee('me'), label: React.createElement(DataPill, { value: `${i18n.t('you')} (${self.title})`, type: DataType.STAFF, avatarUrl: self.profileImageUrl }) }))),
                        !!creatorCanHaveIt && !creatorIsMe && (React.createElement(Horizontal, { justify: 'flex-start', centered: true },
                            React.createElement(Checkbox, { color: Palette.GoodOldBlue, checked: assigneeType === 'reporter', onChange: () => handleChangeAssignee('reporter'), label: React.createElement(DataPill, { value: issue === null || issue === void 0 ? void 0 : issue.createdBy.title, type: DataType.STAFF, avatarUrl: issue === null || issue === void 0 ? void 0 : issue.createdBy.profileImageUrl }) }))),
                        React.createElement(Checkbox, { color: Palette.GoodOldBlue, checked: assigneeType === 'other', onChange: () => handleChangeAssignee('other'), label: i18n.t('someone-else') })),
                    React.createElement(Pane, { paddingX: true, style: { height: 80 } }, assigneeType === 'other' && (React.createElement(SPTChooser, { filters: { place: [(_f = issue === null || issue === void 0 ? void 0 : issue.place) === null || _f === void 0 ? void 0 : _f.id] }, value: otherAllocatee ? [otherAllocatee] : [], onChange: a => handleChangeAssignee('other', a) })))),
                React.createElement(ConversationContainer, { id: issue === null || issue === void 0 ? void 0 : issue.conversation.id, relatedIssueId: issue === null || issue === void 0 ? void 0 : issue.id, editable: false })))));
}
export default IssueReassignDialog;
